import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Package, ClipboardList, Newspaper, FileText, Briefcase, Users, Mail } from "lucide-react";
import { api, GHS, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ContentManager, BLOG_CONFIG, NEWS_CONFIG, JOB_CONFIG, TEAM_CONFIG } from "@/components/admin/ContentManager";
import { Customers } from "@/components/admin/Customers";
import { ImageUpload } from "@/components/ImageUpload";
import { STATUS_LABEL, STATUS_COLOR } from "@/lib/status";
import { toast } from "sonner";

const CATEGORIES = ["Fertilizers", "Pesticides & Insecticides", "Seeds", "Soil Amendments"];
const STATUSES = ["placed", "confirmed", "out_for_delivery", "delivered", "cancelled"];
const EMPTY = { name: "", category: "Fertilizers", description: "", price: "", stock: "", image: "", unit: "", featured: false, composition: "", application: "", crops: "", certifications: "", gallery: [], video: "", tiktok_url: "" };

function ProductForm({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editing ? { ...editing } : EMPTY); }, [editing, open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const addGallery = (url) => url && setForm((f) => ({ ...f, gallery: [...(f.gallery || []), url] }));
  const removeGallery = (i) => setForm((f) => ({ ...f, gallery: (f.gallery || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) };
      if (editing) await api.put(`/products/${editing.product_id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      onSaved(); onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-bg max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl text-brand">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          <DialogDescription className="text-brand-muted">Manage product details, pricing and image.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-brand">Name</Label><Input data-testid="pf-name" value={form.name} onChange={set("name")} className="mt-1.5 h-11 rounded-sm" /></div>
          <div>
            <Label className="text-brand">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="pf-category" className="mt-1.5 h-11 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-brand">Description</Label><Textarea data-testid="pf-desc" value={form.description} onChange={set("description")} className="mt-1.5 rounded-sm" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-brand">Price (GHS)</Label><Input data-testid="pf-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1.5 h-11 rounded-sm" /></div>
            <div><Label className="text-brand">Stock</Label><Input data-testid="pf-stock" type="number" value={form.stock} onChange={set("stock")} className="mt-1.5 h-11 rounded-sm" /></div>
          </div>
          <div><Label className="text-brand">Unit (e.g. 1L bottle)</Label><Input data-testid="pf-unit" value={form.unit} onChange={set("unit")} className="mt-1.5 h-11 rounded-sm" /></div>
          <div><Label className="text-brand">Product Image</Label><div className="mt-1.5"><ImageUpload value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} testid="pf-image" /></div></div>
          <div>
            <Label className="text-brand">Gallery <span className="text-brand-muted font-normal">— extra images shown as thumbnails</span></Label>
            {(form.gallery || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="pf-gallery-list">
                {(form.gallery || []).map((src, i) => (
                  <div key={src} className="relative">
                    <img src={src} alt={`gallery ${i + 1}`} className="w-16 h-16 object-cover rounded-sm border border-brand/10" />
                    <button type="button" data-testid={`pf-gallery-remove-${i}`} onClick={() => removeGallery(i)}
                      className="absolute -top-2 -right-2 bg-brand-terracotta text-white rounded-full p-1 shadow hover:bg-brand-hover">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-2"><ImageUpload value="" onChange={addGallery} testid="pf-gallery-add" /></div>
          </div>
          <div><Label className="text-brand">Video URL <span className="text-brand-muted font-normal">— optional MP4 shown on product page</span></Label><Input data-testid="pf-video" value={form.video} onChange={set("video")} placeholder="https://…mp4" className="mt-1.5 h-11 rounded-sm" /></div>
          <div><Label className="text-brand">TikTok Video URL <span className="text-brand-muted font-normal">— optional, embeds the TikTok on the product page</span></Label><Input data-testid="pf-tiktok" value={form.tiktok_url} onChange={set("tiktok_url")} placeholder="https://www.tiktok.com/@…/video/…" className="mt-1.5 h-11 rounded-sm" /></div>
          <div><Label className="text-brand">Composition</Label><Textarea data-testid="pf-composition" value={form.composition} onChange={set("composition")} className="mt-1.5 rounded-sm" /></div>
          <div><Label className="text-brand">Application & Dosage</Label><Textarea data-testid="pf-application" value={form.application} onChange={set("application")} className="mt-1.5 rounded-sm" /></div>
          <div><Label className="text-brand">Suitable Crops</Label><Textarea data-testid="pf-crops" value={form.crops} onChange={set("crops")} className="mt-1.5 rounded-sm" /></div>
          <div><Label className="text-brand">Certifications</Label><Textarea data-testid="pf-certifications" value={form.certifications} onChange={set("certifications")} className="mt-1.5 rounded-sm" /></div>
          <label className="flex items-center gap-2 text-sm text-brand">
            <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="w-4 h-4 accent-[#1A3B2A]" /> Featured on homepage
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="pf-save" onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => { Promise.all([loadProducts(), loadOrders()]).finally(() => setLoading(false)); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted"); loadProducts();
  };

  const changeStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success("Status updated"); loadOrders();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <h1 className="font-display text-4xl text-brand">Admin Panel</h1>
      <p className="text-brand-muted mt-1">Manage products and fulfil orders.</p>

      <Tabs defaultValue="orders" className="mt-8">
        <TabsList className="bg-brand-surface flex-wrap h-auto">
          <TabsTrigger value="orders" data-testid="tab-orders" className="gap-2"><ClipboardList className="w-4 h-4" /> Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="gap-2"><Package className="w-4 h-4" /> Products ({products.length})</TabsTrigger>
          <TabsTrigger value="blogs" data-testid="tab-blogs" className="gap-2"><FileText className="w-4 h-4" /> Blog</TabsTrigger>
          <TabsTrigger value="news" data-testid="tab-news" className="gap-2"><Newspaper className="w-4 h-4" /> News</TabsTrigger>
          <TabsTrigger value="jobs" data-testid="tab-jobs" className="gap-2"><Briefcase className="w-4 h-4" /> Jobs</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team" className="gap-2"><Users className="w-4 h-4" /> Team</TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="gap-2"><Mail className="w-4 h-4" /> Customers</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? <p className="text-brand-muted py-10 text-center">No orders yet.</p> : (
            <div className="space-y-4" data-testid="admin-orders">
              {orders.map((o) => (
                <div key={o.order_id} className="bg-white border border-brand/10 rounded-sm p-5" data-testid={`admin-order-${o.order_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                      <p className="font-medium text-brand">#{o.order_id} · {o.full_name}</p>
                      <p className="text-xs text-brand-muted mt-0.5">{o.phone} · {o.city}, {o.region}</p>
                      <p className="text-xs text-brand-muted mt-0.5">{new Date(o.created_at).toLocaleString("en-GB")} · {o.items.length} item(s)</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-brand">{GHS(o.total)}</p>
                      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_COLOR[o.status]}`}>{STATUS_LABEL[o.status]}</span>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-brand-muted">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
                  <div className="mt-4 flex items-center gap-3">
                    <Label className="text-xs text-brand-muted">Update status:</Label>
                    <Select value={o.status} onValueChange={(v) => changeStatus(o.order_id, v)}>
                      <SelectTrigger data-testid={`status-select-${o.order_id}`} className="h-9 w-48 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="products" className="mt-6">
          <div className="flex justify-end mb-4">
            <Button data-testid="add-product-btn" onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-brand hover:bg-brand-hover text-white rounded-full gap-1.5"><Plus className="w-4 h-4" /> Add Product</Button>
          </div>
          <div className="grid gap-3" data-testid="admin-products">
            {products.map((p) => (
              <div key={p.product_id} className="bg-white border border-brand/10 rounded-sm p-4 flex items-center gap-4" data-testid={`admin-product-${p.product_id}`}>
                <img src={p.image} alt={p.name} className="w-14 h-14 object-cover rounded-sm bg-brand-surface" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-brand truncate">{p.name} {p.featured && <span className="text-[10px] bg-brand-ochre/20 text-brand px-2 py-0.5 rounded-full ml-1">Featured</span>}</p>
                  <p className="text-xs text-brand-muted">{p.category} · {p.unit} · Stock: {p.stock}</p>
                </div>
                <span className="font-bold text-brand">{GHS(p.price)}</span>
                <button data-testid={`edit-product-${p.product_id}`} onClick={() => { setEditing(p); setFormOpen(true); }} className="p-2 text-brand hover:bg-brand-surface rounded-full"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-product-${p.product_id}`} onClick={() => remove(p.product_id)} className="p-2 text-brand-terracotta hover:bg-brand-surface rounded-full"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="blogs" className="mt-6"><ContentManager config={BLOG_CONFIG} /></TabsContent>
        <TabsContent value="news" className="mt-6"><ContentManager config={NEWS_CONFIG} /></TabsContent>
        <TabsContent value="jobs" className="mt-6"><ContentManager config={JOB_CONFIG} /></TabsContent>
        <TabsContent value="team" className="mt-6"><ContentManager config={TEAM_CONFIG} /></TabsContent>
        <TabsContent value="customers" className="mt-6"><Customers /></TabsContent>
      </Tabs>

      <ProductForm open={formOpen} onOpenChange={setFormOpen} editing={editing} onSaved={loadProducts} />
    </div>
  );
}
